import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { InitialDataResponseInterface } from '../services/exports/Interfaces';
import InitialLoadingManager from '../services/api/InitialLoadingManager';
import { DataWrappedResponse } from '../services/exports/Types';
import { InitialDataStoreInterface, StoreInterface } from '../store/types';
import useAuth from '../hooks/global/useAuth';
import { useIsMount } from '../hooks/utility/useIsMount';

interface Props {
  children: any;
}

export interface InitialDataContextInterface {
  data: InitialDataStoreInterface;
  loading: boolean;
  refresh: (
    slug?: null | string,
    hideLoading?: boolean,
  ) => Promise<DataWrappedResponse<InitialDataResponseInterface | InitialDataStoreInterface>>;
  forceRefresh: (
    slug?: null | string,
    hideLoading?: boolean,
  ) => Promise<DataWrappedResponse<InitialDataResponseInterface | InitialDataStoreInterface>>;
}

export const InitialDataContext = React.createContext<InitialDataContextInterface>(null);

export default function InitialDataProvider(props: Props) {
  const { children } = props;

  const { initialData: data } = useSelector((state: StoreInterface) => state);

  const isMount = useIsMount();
  const { isLoggedIn } = useAuth();

  const [loading, setLoading] = useState(false);
  const toggleLoading = () => setLoading((prev) => !prev);

  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    !isMount && isLoggedIn && forceRefresh();
  }, [isLoggedIn]);

  const refresh = async (
    slug: string = null,
    hideLoading: boolean = false,
  ): Promise<DataWrappedResponse<InitialDataResponseInterface | InitialDataStoreInterface>> => {
    if (loadingRef.current) {
      return {
        success: true,
        // @ts-expect-error Response is already being loaded
        response: {
          data: { data },
        },
        status: 200,
      };
    }

    return forceRefresh(slug, hideLoading);
  };

  async function forceRefresh(
    slug: string = null,
    hideLoading: boolean = false,
  ): Promise<DataWrappedResponse<InitialDataResponseInterface | InitialDataStoreInterface>> {
    toggleLoading();
    const response = await InitialLoadingManager.init(slug ?? dataRef.current?.company?.slug, hideLoading);
    setTimeout(() => toggleLoading(), 300);

    return response;
  }

  const context = useMemo(
    () => ({
      data: dataRef.current,
      loading: loadingRef.current,
      refresh,
      forceRefresh,
    }),
    [dataRef.current, loadingRef.current],
  );

  return <InitialDataContext.Provider value={context}>{children}</InitialDataContext.Provider>;
}
